/**
 * @fileoverview Uncaught-error capture for the browser logger (UNREACHABLE-MODULE-BURNDOWN-001
 * Q-UMB-13).
 *
 * BROWSER-TELEMETRY-001 wired the logger to ship, but only what code chose to LOG. An exception
 * nobody caught — a throw in an event handler, a rejected promise nobody awaited, and under React
 * 19 every render error no boundary caught (the root reports it through `reportError`, which fires
 * `error` on `window`) — reached no shared capture in any of the ten app bundles (measured
 * 2026-09-25: pos-web alone had one, inside `<App>` — blind during bootstrap and passing the raw
 * `Error` as data — and the nine marketing entries ran `createErrorReporter` with no transport).
 * Those are the errors real-user monitoring exists for.
 *
 * This installs the two window listeners and routes each event through `logger.error`, so it
 * rides the SAME pipeline as every other entry — sanitisation, trace ids, the shipping policy,
 * storm suppression. There is no second transport here by design. It is installed by
 * `createAppLogger`, the one bootstrap every web entry calls first, so no app wires it.
 *
 * An `Error` is never passed as `data` (LOGGER-CANON-001: `sanitizeData` reduces it to `{}`); its
 * name, message and stack are copied out.
 *
 * @module captureGlobalErrors
 */

/** One installation per event target; a re-install replaces it (HMR, a second bootstrap). */
const installed = new WeakMap();

/**
 * Captured-error kinds, as they appear in `data.kind` — the window's two (this module) and the
 * React root's two (`./reactRootErrorOptions.js`): one vocabulary for every error this package
 * captures without the app logging it.
 */
export const GLOBAL_ERROR_KIND = Object.freeze({
  UNCAUGHT_ERROR: 'uncaught-error',
  UNHANDLED_REJECTION: 'unhandled-rejection',
  CAUGHT_RENDER_ERROR: 'caught-render-error',
  RECOVERABLE_RENDER_ERROR: 'recoverable-render-error',
});

/**
 * The name, message and stack of a thrown value, copied OUT — an `Error` passed as log `data` is
 * reduced to `{}` by `sanitizeData` (LOGGER-CANON-001). A non-Error throw is named by its type.
 *
 * @param {unknown} value - what was thrown or rejected
 * @returns {{name: string, message: string, stack: string|null}}
 */
export function describeError(value) {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack ?? null };
  }
  return { name: typeof value, message: String(value), stack: null };
}

function errorEventData(event) {
  const where = { filename: event.filename || null, lineno: event.lineno, colno: event.colno };
  if (event.error !== undefined && event.error !== null) {
    return { kind: GLOBAL_ERROR_KIND.UNCAUGHT_ERROR, ...describeError(event.error), ...where };
  }
  // A cross-origin script's error reaches the page as the opaque "Script error." with no Error
  // object — still worth one line: it says a third-party script is failing on this page.
  return {
    kind: GLOBAL_ERROR_KIND.UNCAUGHT_ERROR,
    name: 'ScriptError',
    message: event.message || 'Script error.',
    stack: null,
    crossOrigin: true,
    ...where,
  };
}

/**
 * Route uncaught errors and unhandled promise rejections on `target` to `logger.error`.
 *
 * @param {Object} options
 * @param {{error: Function}} options.logger - the configured logger
 * @param {EventTarget|null} [options.target=globalThis] - the window; `null` for a non-browser host
 * @returns {() => void} uninstall
 * @throws {TypeError} when the logger cannot record an error
 */
export function installGlobalErrorCapture({ logger, target = globalThis } = {}) {
  if (!logger || typeof logger.error !== 'function') {
    throw new TypeError('installGlobalErrorCapture requires a logger with an `error` method');
  }
  if (!target || typeof target.addEventListener !== 'function') return () => {};
  installed.get(target)?.();

  let reporting = false;
  function report(data) {
    // A logger that throws while reporting must neither recurse into this handler nor escape it.
    if (reporting) return;
    reporting = true;
    try {
      const prefix =
        data.kind === GLOBAL_ERROR_KIND.UNHANDLED_REJECTION ? 'Unhandled rejection' : 'Uncaught';
      logger.error(`${prefix} ${data.name}: ${data.message}`, data);
    } catch (_logErr) {
      // Telemetry must never become the page's second failure.
    } finally {
      reporting = false;
    }
  }

  const onError = (event) => report(errorEventData(event));
  const onRejection = (event) =>
    report({ kind: GLOBAL_ERROR_KIND.UNHANDLED_REJECTION, ...describeError(event.reason) });

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  const uninstall = () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    if (installed.get(target) === uninstall) installed.delete(target);
  };
  installed.set(target, uninstall);
  return uninstall;
}
